/**
 * Member Events — what's coming up, then what they've already been part of.
 *
 * History is deliberately quieter than the future: lighter cards at reduced
 * opacity so upcoming work stays dominant.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { KIND_LABEL, today } from '../../data';
import { useClubData } from '../../context/ClubContext';
import { EventCard, HistoryRow, trendingEventId } from '../../components/EventCard';
import {
  Empty,
  Eyebrow,
  Pill,
  SummaryCell,
  SummaryRow,
  ViewTitle,
  YearSplit,
} from '../../components/ui';

export function MemberEventsPage() {
  const { db, meId } = useClubData();
  const navigate = useNavigate();
  const now = today();

  const upcoming = useMemo(
    () => db.events.filter((e) => e.date >= now).sort((a, b) => a.date.localeCompare(b.date)),
    [db.events, now],
  );
  const hot = useMemo(() => trendingEventId(db.events, now), [db.events, now]);

  /**
   * "Took part" means one of three things: they RSVP'd, they signed up for a
   * department on it, or they have logged hours against it.
   */
  const past = useMemo(() => {
    const myDeptIds = db.signups.filter((s) => s.memberId === meId).map((s) => s.deptId);
    return db.events
      .filter((e) => e.date < now)
      .filter((e) => {
        const rsvpd = e.rsvp.includes(meId ?? '');
        const signedUp = db.departments.some(
          (d) => d.eventId === e.id && myDeptIds.includes(d.id),
        );
        const logged = db.volunteer.some((v) => v.memberId === meId && v.eventId === e.id);
        return rsvpd || signedUp || logged;
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [db, meId, now]);

  const byYear = useMemo(() => {
    // year ("2026") -> events in that year
    const groups = {};
    for (const e of past) {
      const year = e.date.slice(0, 4);
      (groups[year] ??= []).push(e);
    }
    return groups;
  }, [past]);

  const years = Object.keys(byYear).sort((a, b) => b.localeCompare(a));
  const rsvpCount = past.filter((e) => e.rsvp.includes(meId ?? '')).length;
  const volCount = past.filter((e) =>
    db.volunteer.some((v) => v.memberId === meId && v.eventId === e.id),
  ).length;

  return (
    <>
      <ViewTitle title="Events" sub="Group runs, races, meetings and volunteer duty" />

      <Eyebrow>Coming up</Eyebrow>
      {upcoming.length ? (
        upcoming.map((e) => (
          <EventCard
            key={e.id}
            event={e}
            db={db}
            meId={meId}
            trending={e.id === hot}
            onOpen={(id) => navigate(`/m/events/${id}`)}
          />
        ))
      ) : (
        <Empty title="Nothing scheduled">Check back soon.</Empty>
      )}

      <Eyebrow>Your history</Eyebrow>
      <SummaryRow>
        <SummaryCell value={past.length} label="Events taken part in" />
        <SummaryCell value={rsvpCount} label="Runs & meetings" />
        {/* sea-600 — volunteering is the one number that earns colour here */}
        <SummaryCell value={volCount} label="Volunteered at" color="#059669" />
      </SummaryRow>

      {past.length ? (
        years.map((year) => (
          <div key={year}>
            <YearSplit>{year}</YearSplit>
            {byYear[year].map((e) => {
              const hours = db.volunteer
                .filter((v) => v.memberId === meId && v.eventId === e.id)
                .reduce((sum, v) => sum + v.hours, 0);
              const didVolunteer = hours > 0;

              return (
                <HistoryRow
                  key={e.id}
                  date={e.date}
                  title={e.title}
                  meta={`${KIND_LABEL[e.kind]} · ${e.venue}`}
                  onClick={() => navigate(`/m/events/${e.id}`)}
                  tag={
                    didVolunteer ? (
                      <Pill tone="ok">Volunteered · {hours}h</Pill>
                    ) : (
                      <Pill>Took part</Pill>
                    )
                  }
                />
              );
            })}
          </div>
        ))
      ) : (
        <Empty title="No history yet" padding={34}>
          RSVP to a run above and it will appear here afterwards.
        </Empty>
      )}
    </>
  );
}
